import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, IconButton, CircularProgress, Alert, Snackbar, Divider, Chip, Avatar,
} from '@mui/material';
import {
  ArrowBackRounded, AddRounded, DeleteOutlineRounded, CheckRounded,
  PersonRounded, ConfirmationNumberRounded, HistoryRounded, MedicalServicesRounded,
} from '@mui/icons-material';
import { colors, brand } from '../../theme/theme';
import { useTokens } from '../../hooks/useTokens';
import { useConsultation, PrescriptionItem, MedicalTest, Consultation } from '../../hooks/useConsultation';
import { Token, TOKEN_STATUS_META, tokenCustomerName } from '../../api/tokenTypes';
import { initials } from '../../auth/session';

const card = { bgcolor: colors.backgroundPaper, border: `1px solid ${colors.border}`, borderRadius: '14px', p: 2.5 };
const emptyRx = (): PrescriptionItem => ({ medicine_name: '', dosage: '', frequency: '', times_per_day: null, duration_days: null, notes: '' });
const emptyTest = (): MedicalTest => ({ test_name: '', notes: '' });
const num = (v: string): number | null => (v === '' ? null : Number(v));
const fmtDate = (d?: string | null) => (d ? new Date(d.length <= 10 ? d + 'T00:00:00' : d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const ConsultationScreen: React.FC = () => {
  const { id: queueId, tokenId } = useParams();
  const navigate = useNavigate();
  const { getToken } = useTokens();
  const { getConsultation, saveConsultation, getHistory } = useConsultation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [token, setToken] = useState<Token | null>(null);
  const [patient, setPatient] = useState<{ name: string | null; country_code: string | null; phone: string | null } | null>(null);
  const [followUp, setFollowUp] = useState('');
  const [notes, setNotes] = useState('');
  const [rx, setRx] = useState<PrescriptionItem[]>([emptyRx()]);
  const [tests, setTests] = useState<MedicalTest[]>([emptyTest()]);
  const [history, setHistory] = useState<Array<Consultation & { prescriptions: PrescriptionItem[]; tests: MedicalTest[] }>>([]);

  const loadHistory = useCallback(async (cc: string, phone: string) => {
    try { setHistory(await getHistory(cc, phone)); } catch { /* ignore */ }
  }, [getHistory]);

  useEffect(() => {
    if (!tokenId) return;
    let active = true;
    (async () => {
      try {
        const [tk, bundle] = await Promise.all([getToken(tokenId), getConsultation(tokenId)]);
        if (!active) return;
        setToken(tk);
        setPatient(bundle.patient);
        setFollowUp(bundle.consultation?.follow_up_date ? bundle.consultation.follow_up_date.slice(0, 10) : '');
        setNotes(bundle.consultation?.notes || '');
        setRx(bundle.prescriptions.length ? bundle.prescriptions.map((p) => ({ ...p })) : [emptyRx()]);
        setTests(bundle.tests.length ? bundle.tests.map((t) => ({ ...t })) : [emptyTest()]);
        const cc = bundle.patient?.country_code || '+91';
        const phone = bundle.patient?.phone;
        if (phone) loadHistory(cc, phone);
      } catch (e: any) {
        if (active) setError(e?.response?.data?.message || 'Failed to load consultation.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [tokenId, getToken, getConsultation, loadHistory]);

  const setRxField = (i: number, k: keyof PrescriptionItem, v: any) => setRx((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const setTestField = (i: number, k: keyof MedicalTest, v: any) => setTests((p) => p.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const save = async () => {
    if (!tokenId) return;
    const cleanRx = rx.filter((r) => r.medicine_name.trim() || r.dosage || r.frequency || r.times_per_day || r.duration_days || r.notes);
    const cleanTests = tests.filter((t) => t.test_name.trim() || t.notes);
    if (cleanRx.some((r) => !r.medicine_name.trim())) return setError('Every prescription needs a medicine name.');
    if (cleanTests.some((t) => !t.test_name.trim())) return setError('Every test needs a name.');
    setSaving(true); setError(null);
    try {
      await saveConsultation(tokenId, {
        follow_up_date: followUp || null, notes: notes || null,
        prescriptions: cleanRx.map((r) => ({ medicine_name: r.medicine_name.trim(), dosage: r.dosage || null, frequency: r.frequency || null, times_per_day: r.times_per_day ?? null, duration_days: r.duration_days ?? null, notes: r.notes || null })),
        tests: cleanTests.map((t) => ({ test_name: t.test_name.trim(), notes: t.notes || null })),
      });
      setToast('Consultation saved');
      if (patient?.phone) loadHistory(patient.country_code || '+91', patient.phone);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to save consultation.'); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  const statusMeta = token ? TOKEN_STATUS_META[token.status] : null;
  const infoRow = (label: string, value: React.ReactNode) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: `1px solid ${colors.border}` }}>
      <Typography sx={{ color: colors.textSecondary, fontSize: 13 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 600, fontSize: 13, textAlign: 'right' }}>{value ?? '—'}</Typography>
    </Box>
  );

  return (
    <Box>
      <Button startIcon={<ArrowBackRounded />} variant="text" onClick={() => navigate(`/queues/${queueId}`)} sx={{ mb: 2 }}>Back to queue</Button>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Customer + token info */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25, mb: 2.25 }}>
        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}><PersonRounded sx={{ color: colors.primary }} /><Typography variant="h5">Customer</Typography></Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Avatar sx={{ width: 44, height: 44, background: brand.gradProvider }}>{initials(token ? tokenCustomerName(token) : patient?.name || '?')}</Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700 }}>{token ? tokenCustomerName(token) : patient?.name}</Typography>
              <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{patient?.country_code} {patient?.phone}</Typography>
            </Box>
          </Box>
          {infoRow('Total visits', token?.total_visits ?? '—')}
          {infoRow('Last visit', fmtDate(token?.last_visit))}
        </Box>

        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}><ConfirmationNumberRounded sx={{ color: colors.primary }} /><Typography variant="h5">Token</Typography></Box>
          {infoRow('Token #', token?.token_number)}
          {infoRow('Status', statusMeta ? <Chip size="small" label={statusMeta.label} sx={{ bgcolor: `${statusMeta.color}1F`, color: statusMeta.color, fontWeight: 600, height: 22 }} /> : '—')}
          {infoRow('Type', token?.token_type === 'walk_in' ? 'Walk-in' : 'Pre-booked')}
          {infoRow('Provider', token?.provider?.display_name)}
          {infoRow('Date', fmtDate(token?.scheduled_date))}
        </Box>
      </Box>

      {/* Past history */}
      <Box sx={{ ...card, mb: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}><HistoryRounded sx={{ color: colors.primary }} /><Typography variant="h5">Past visits {history.length ? `(${history.length})` : ''}</Typography></Box>
        {history.length === 0 ? (
          <Typography sx={{ color: colors.textSecondary, fontSize: 14 }}>No earlier consultations for this patient.</Typography>
        ) : history.map((h) => (
          <Box key={h.id} sx={{ py: 1.25, borderBottom: `1px solid ${colors.border}` }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtDate(h.created_at)}{h.follow_up_date ? ` · follow-up ${fmtDate(h.follow_up_date)}` : ''}</Typography>
            {h.notes && <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>{h.notes}</Typography>}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {h.prescriptions.map((p, k) => <Chip key={k} size="small" label={`${p.medicine_name}${p.dosage ? ` ${p.dosage}` : ''}`} sx={{ bgcolor: '#EFF6FF', color: '#065CC6' }} />)}
              {h.tests.map((t, k) => <Chip key={`t${k}`} size="small" label={t.test_name} sx={{ bgcolor: '#FFFBEB', color: '#B45309' }} />)}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Current consultation form */}
      <Box sx={card}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}><MedicalServicesRounded sx={{ color: colors.primary }} /><Typography variant="h5">New consultation</Typography></Box>

        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Prescriptions</Typography>
        {rx.map((r, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1.4fr 0.7fr 0.8fr auto' }, gap: 1, mb: 1 }}>
            <TextField size="small" label="Medicine" value={r.medicine_name} onChange={(e) => setRxField(i, 'medicine_name', e.target.value)} />
            <TextField size="small" label="Dosage" placeholder="500mg" value={r.dosage || ''} onChange={(e) => setRxField(i, 'dosage', e.target.value)} />
            <TextField size="small" label="Frequency" placeholder="Twice daily after food" value={r.frequency || ''} onChange={(e) => setRxField(i, 'frequency', e.target.value)} />
            <TextField size="small" type="number" label="x/day" value={r.times_per_day ?? ''} onChange={(e) => setRxField(i, 'times_per_day', num(e.target.value))} />
            <TextField size="small" type="number" label="Days" value={r.duration_days ?? ''} onChange={(e) => setRxField(i, 'duration_days', num(e.target.value))} />
            <IconButton onClick={() => setRx((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : [emptyRx()]))}><DeleteOutlineRounded /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddRounded />} onClick={() => setRx((p) => [...p, emptyRx()])}>Add medicine</Button>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Recommended tests</Typography>
        {tests.map((t, i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 2fr auto' }, gap: 1, mb: 1 }}>
            <TextField size="small" label="Test name" value={t.test_name} onChange={(e) => setTestField(i, 'test_name', e.target.value)} />
            <TextField size="small" label="Notes (optional)" value={t.notes || ''} onChange={(e) => setTestField(i, 'notes', e.target.value)} />
            <IconButton onClick={() => setTests((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : [emptyTest()]))}><DeleteOutlineRounded /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<AddRounded />} onClick={() => setTests((p) => [...p, emptyTest()])}>Add test</Button>

        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, gap: 2 }}>
          <TextField size="small" type="date" label="Follow-up date" InputLabelProps={{ shrink: true }} value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
          <TextField size="small" label="Consultation notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} multiline minRows={1} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
          <Button variant="contained" startIcon={<CheckRounded />} disabled={saving} onClick={save}>{saving ? <CircularProgress size={20} color="inherit" /> : 'Save consultation'}</Button>
        </Box>
      </Box>

      <Snackbar open={!!toast} autoHideDuration={2500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsultationScreen;
